import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'

import Impact from './goal/Impact'
import Goal   from './goal/Goal'
import Top    from './donations/Top'
import All    from './donations/All'

import { unique } from './donations/useInfiniteOrders'
import { onBroadcastMessage } from 'src/utils/messages/broadcast'
import mountIfExists from 'src/utils/mountIfExists'

const INTERVAL = 30000

const App = props => {
  const [ flp, setFlp ] = useState(props.fundraiserLandingPage)
  const [ impactWidget, setImpactWidget ] = useState(props.impactWidget)
  const [ orders, setOrders ] = useState(props.orders)
  const [ topOrders, setTopOrders ] = useState(props.top_orders)
  const [ pageHidden, setPageHidden ] = useState(document.hidden)
  const [ needsRefresh, setNeedsRefresh ] = useState(false)

  const fetchRecentDonations = async order => {
    setNeedsRefresh(false)

    const response = await fetch(location.pathname,
      { headers: { Accept: 'application/json'} })

    if (response.ok) {
      const data = await response.json()
      setFlp(data.fundraiser_landing_page)
      setImpactWidget(data.impact_widget)
      setOrders(
        order
        ? unique(data.orders, [ order ]).concat(data.orders).sort((a, b) => b.id - a.id)
        : data.orders
      )
      setTopOrders(data.top_orders)
    }
  }

  useEffect(() => {
    if (pageHidden) return

    if (needsRefresh) fetchRecentDonations()
    const interval = setInterval(fetchRecentDonations, INTERVAL)
    return () => clearInterval(interval)
  }, [ pageHidden, needsRefresh ])

  useEffect(() => {
    if (!pageHidden) return

    const timeout = setTimeout(() => setNeedsRefresh(true), INTERVAL)
    return () => clearTimeout(timeout)
  }, [ pageHidden ])

  useEffect(() => {
    const handleVisibilityChange = () => setPageHidden(document.hidden)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  // `payload.order` is always undefined now: every widget path flattens the
  // serialized order into the completed-donation detail rather than nesting it
  // (v6 DAF was the last holdout). So this is effectively a plain refetch — the
  // `order ?` optimistic-prepend branch above is dead. Harmless, since the order
  // is persisted before the response and so comes back in `data.orders`.
  //
  // If you revive the prepend, don't just pass `payload`: unique() dedupes on
  // `id` (donations/useInfiniteOrders.js), and the paths with no serialized order
  // — v6 stock hands over just `{ partnerItem }` — carry no id, so
  // `ids.includes(undefined)` is false and the payload would be prepended as a
  // junk row. Guard on `payload.id` before passing it.
  useEffect(() =>
    onBroadcastMessage('donationCompleted', payload =>
      fetchRecentDonations(payload.order)
    )
  , [])

  return (
    <div>
      {
        Array.prototype.map.call(
          document.querySelectorAll('.GoalRoot'),
          (goalRoot, i) => {
            const placeholder = goalRoot.querySelector('.placeholder')
            placeholder && goalRoot.removeChild(placeholder)

            return createPortal(
              impactWidget
              ? <Impact key={i}
                  {...impactWidget}
                  goal={flp.goal} // replace with goal on impact widget later
                />
              : <Goal key={i} {...flp} />,
              goalRoot
            )
          }
        )
      }
      {
        mountIfExists('RecentDonationsRoot', () =>
          <All
            orders={orders}
            more_orders_available={props.more_orders_available}
            organizer={props.organizer}
            logged_in={props.logged_in}
            flpSigninPath={props.flpSigninPath}
          />
        )
      }
      {
        mountIfExists('TopDonationsRoot', () =>
          <Top
            topOrders={topOrders}
            organizer={props.organizer}
            logged_in={props.logged_in}
            flpSigninPath={props.flpSigninPath}
          />
        )
      }
    </div>
  )
}

export default App
